/**
 * Shadow and Glow System
 *
 * Provides elevation shadows and glow effects for Liquid Glass UI.
 * Apple-inspired layered shadows with specular highlights and softened accent glows.
 */

import { baseColors } from './palette';

// ============================================================================
// Elevation Shadows (Light Theme)
// ============================================================================

export const lightShadows = {
  // No elevation
  0: 'none',

  // Subtle elevation (cards, inputs)
  1: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',

  // Low elevation (hover states)
  2: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',

  // Medium elevation (dropdowns, popovers)
  3: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',

  // High elevation (modals, dialogs)
  4: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',

  // Very high elevation (notifications, toasts)
  5: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',

  // Maximum elevation (full-screen overlays)
  6: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
} as const;

// ============================================================================
// Elevation Shadows (Dark Theme)
// ============================================================================

export const darkShadows = {
  0: 'none',
  1: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  2: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px -1px rgba(0, 0, 0, 0.4)',
  3: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -2px rgba(0, 0, 0, 0.4)',
  4: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -4px rgba(0, 0, 0, 0.4)',
  5: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
  6: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
} as const;

// ============================================================================
// Glow Effects
// ============================================================================

export const glowEffects = {
  // Primary glow (buttons, active elements) — softened for Liquid Glass
  primary: {
    subtle: `0 0 16px rgba(${hexToRgb(baseColors.primary[500])}, 0.10)`,
    medium: `0 0 24px rgba(${hexToRgb(baseColors.primary[500])}, 0.16)`,
    strong: `0 0 32px rgba(${hexToRgb(baseColors.primary[500])}, 0.22)`,
    intense: `0 0 48px rgba(${hexToRgb(baseColors.primary[500])}, 0.30)`,
  },

  // Secondary glow (accent elements)
  secondary: {
    subtle: `0 0 16px rgba(${hexToRgb(baseColors.secondary[500])}, 0.10)`,
    medium: `0 0 24px rgba(${hexToRgb(baseColors.secondary[500])}, 0.16)`,
    strong: `0 0 32px rgba(${hexToRgb(baseColors.secondary[500])}, 0.22)`,
    intense: `0 0 48px rgba(${hexToRgb(baseColors.secondary[500])}, 0.30)`,
  },

  // Success glow
  success: {
    subtle: `0 0 16px rgba(${hexToRgb(baseColors.success[500])}, 0.10)`,
    medium: `0 0 24px rgba(${hexToRgb(baseColors.success[500])}, 0.16)`,
  },

  // Error glow
  error: {
    subtle: `0 0 16px rgba(${hexToRgb(baseColors.error[500])}, 0.10)`,
    medium: `0 0 24px rgba(${hexToRgb(baseColors.error[500])}, 0.16)`,
  },

  // Teal glow (for twilight theme)
  teal: {
    subtle: `0 0 16px rgba(${hexToRgb(baseColors.teal[500])}, 0.10)`,
    medium: `0 0 24px rgba(${hexToRgb(baseColors.teal[500])}, 0.16)`,
    strong: `0 0 32px rgba(${hexToRgb(baseColors.teal[500])}, 0.22)`,
    intense: `0 0 48px rgba(${hexToRgb(baseColors.teal[500])}, 0.30)`,
  },

  // Mixed glow (magical effect) — softened
  mixed: {
    subtle: `0 0 24px rgba(${hexToRgb(baseColors.primary[500])}, 0.08), 0 0 48px rgba(${hexToRgb(baseColors.secondary[500])}, 0.05)`,
    medium: `0 0 32px rgba(${hexToRgb(baseColors.primary[500])}, 0.13), 0 0 64px rgba(${hexToRgb(baseColors.secondary[500])}, 0.10)`,
    strong: `0 0 40px rgba(${hexToRgb(baseColors.primary[500])}, 0.20), 0 0 80px rgba(${hexToRgb(baseColors.secondary[500])}, 0.14)`,
  },
} as const;

// ============================================================================
// Glassmorphism Effects
// ============================================================================

export const glassEffects = {
  // Light theme glass — Liquid Glass material (50% translucency, specular highlights)
  light: {
    background: 'rgba(255, 255, 255, 0.50)',
    backgroundHover: 'rgba(255, 255, 255, 0.62)',
    border: 'rgba(255, 255, 255, 0.35)',
    borderHover: 'rgba(255, 255, 255, 0.50)',
    backdropBlur: 'blur(24px) saturate(180%)',
    shadow: `0 4px 30px rgba(0, 0, 0, 0.05), inset 0 0.5px 0 rgba(255, 255, 255, 0.6)`,
    shadowHover: `0 8px 40px rgba(0, 0, 0, 0.08), inset 0 0.5px 0 rgba(255, 255, 255, 0.7)`,
  },

  // Dark theme glass — Liquid Glass material (subtle translucent surface)
  dark: {
    background: 'rgba(28, 28, 30, 0.12)',
    backgroundHover: 'rgba(28, 28, 30, 0.20)',
    border: 'rgba(255, 255, 255, 0.04)',
    borderHover: 'rgba(255, 255, 255, 0.08)',
    backdropBlur: 'blur(24px) saturate(180%)',
    shadow: `0 4px 30px rgba(0, 0, 0, 0.08), inset 0 0.5px 0 rgba(255, 255, 255, 0.05)`,
    shadowHover: `0 8px 40px rgba(0, 0, 0, 0.14), inset 0 0.5px 0 rgba(255, 255, 255, 0.08)`,
  },

  // Frosted glass (stronger blur, clear variant)
  frosted: {
    background: 'rgba(255, 255, 255, 0.25)',
    backdropBlur: 'blur(40px) saturate(180%)',
    border: 'rgba(255, 255, 255, 0.2)',
  },

  // Twilight theme glass (blue-tinted Liquid Glass)
  twilight: {
    background: 'rgba(15, 22, 33, 0.40)',
    backgroundHover: 'rgba(20, 29, 43, 0.52)',
    border: 'rgba(148, 163, 184, 0.06)',
    borderHover: 'rgba(148, 163, 184, 0.10)',
    backdropBlur: 'blur(24px) saturate(180%)',
    shadow: `0 4px 30px rgba(0, 0, 0, 0.14), inset 0 0.5px 0 rgba(148, 163, 184, 0.06)`,
    shadowHover: `0 8px 40px rgba(0, 0, 0, 0.20), inset 0 0.5px 0 rgba(148, 163, 184, 0.08)`,
  },

  // Midnight theme glass (deep Liquid Glass)
  midnight: {
    background: 'rgba(20, 20, 20, 0.35)',
    backgroundHover: 'rgba(30, 30, 30, 0.45)',
    border: 'rgba(255, 255, 255, 0.05)',
    borderHover: 'rgba(255, 255, 255, 0.08)',
    backdropBlur: 'blur(24px) saturate(180%)',
    shadow: '0 4px 30px rgba(0, 0, 0, 0.18), inset 0 0.5px 0 rgba(255, 255, 255, 0.04)',
    shadowHover: '0 8px 40px rgba(0, 0, 0, 0.26), inset 0 0.5px 0 rgba(255, 255, 255, 0.06)',
  },
} as const;

// ============================================================================
// Inner Shadows (for inset effects)
// ============================================================================

export const innerShadows = {
  subtle: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
  medium: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
  strong: 'inset 0 4px 8px rgba(0, 0, 0, 0.15)',
} as const;

// ============================================================================
// Focus Ring Shadows
// ============================================================================

export const focusRings = {
  primary: `0 0 0 3px rgba(${hexToRgb(baseColors.primary[500])}, 0.25)`,
  secondary: `0 0 0 3px rgba(${hexToRgb(baseColors.secondary[500])}, 0.25)`,
  error: `0 0 0 3px rgba(${hexToRgb(baseColors.error[500])}, 0.25)`,
  neutral: '0 0 0 3px rgba(0, 0, 0, 0.1)',
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

const GLOW_OPACITIES = {
  subtle: 0.1,
  medium: 0.16,
  strong: 0.22,
  intense: 0.3,
} as const;

const GLOW_SIZES = {
  subtle: 16,
  medium: 24,
  strong: 32,
  intense: 48,
} as const;

/**
 * Create a custom glow effect
 */
export function createGlow(color: string, intensity: 'subtle' | 'medium' | 'strong' | 'intense' = 'medium'): string {
  const rgb = hexToRgb(color);
  return `0 0 ${GLOW_SIZES[intensity]}px rgba(${rgb}, ${GLOW_OPACITIES[intensity]})`;
}

/**
 * Combine shadow with glow effect
 */
export function shadowWithGlow(
  shadow: string,
  glowColor: string,
  glowIntensity: 'subtle' | 'medium' | 'strong' = 'subtle',
): string {
  return `${shadow}, ${createGlow(glowColor, glowIntensity)}`;
}

// ============================================================================
// Neon Glow Effects (for NeonCard components)
// ============================================================================

/**
 * Neon glow shadows for each accent color.
 * Each has three intensities: subtle, medium, strong.
 *
 * Includes both semantic names (neutral, success, warning, error, info, secondary)
 * and legacy color names (cyan, purple, green, amber, pink, red) for compatibility.
 */
export const neonGlow = {
  // Semantic: Neutral — softened for Liquid Glass
  neutral: {
    subtle: '0 0 8px rgba(100, 116, 139, 0.10), 0 0 16px rgba(100, 116, 139, 0.05)',
    medium: '0 0 12px rgba(100, 116, 139, 0.14), 0 0 24px rgba(100, 116, 139, 0.07)',
    strong: '0 0 16px rgba(100, 116, 139, 0.18), 0 0 32px rgba(100, 116, 139, 0.08)',
  },
  // Semantic: Primary (purple-blue #635BFF)
  primary: {
    subtle: '0 0 8px rgba(99, 91, 255, 0.12), 0 0 16px rgba(99, 91, 255, 0.06)',
    medium: '0 0 12px rgba(99, 91, 255, 0.18), 0 0 24px rgba(99, 91, 255, 0.10)',
    strong: '0 0 16px rgba(99, 91, 255, 0.25), 0 0 32px rgba(99, 91, 255, 0.14)',
  },
  // Semantic: Success (emerald #10B981)
  success: {
    subtle: '0 0 8px rgba(16, 185, 129, 0.12), 0 0 16px rgba(16, 185, 129, 0.06)',
    medium: '0 0 12px rgba(16, 185, 129, 0.18), 0 0 24px rgba(16, 185, 129, 0.10)',
    strong: '0 0 16px rgba(16, 185, 129, 0.25), 0 0 32px rgba(16, 185, 129, 0.14)',
  },
  // Semantic: Warning (amber #F59E0B)
  warning: {
    subtle: '0 0 8px rgba(245, 158, 11, 0.12), 0 0 16px rgba(245, 158, 11, 0.06)',
    medium: '0 0 12px rgba(245, 158, 11, 0.18), 0 0 24px rgba(245, 158, 11, 0.10)',
    strong: '0 0 16px rgba(245, 158, 11, 0.25), 0 0 32px rgba(245, 158, 11, 0.14)',
  },
  // Semantic: Error (rose #F43F5E)
  error: {
    subtle: '0 0 8px rgba(239, 68, 68, 0.12), 0 0 16px rgba(239, 68, 68, 0.06)',
    medium: '0 0 12px rgba(239, 68, 68, 0.18), 0 0 24px rgba(239, 68, 68, 0.10)',
    strong: '0 0 16px rgba(239, 68, 68, 0.25), 0 0 32px rgba(239, 68, 68, 0.14)',
  },
  // Semantic: Info (sky #0EA5E9)
  info: {
    subtle: '0 0 8px rgba(14, 165, 233, 0.12), 0 0 16px rgba(14, 165, 233, 0.06)',
    medium: '0 0 12px rgba(14, 165, 233, 0.18), 0 0 24px rgba(14, 165, 233, 0.10)',
    strong: '0 0 16px rgba(14, 165, 233, 0.25), 0 0 32px rgba(14, 165, 233, 0.14)',
  },
  // Semantic: Secondary (purple #8B5CF6)
  secondary: {
    subtle: '0 0 8px rgba(139, 92, 246, 0.12), 0 0 16px rgba(139, 92, 246, 0.06)',
    medium: '0 0 12px rgba(139, 92, 246, 0.18), 0 0 24px rgba(139, 92, 246, 0.10)',
    strong: '0 0 16px rgba(139, 92, 246, 0.25), 0 0 32px rgba(139, 92, 246, 0.14)',
  },
  // Decorative: Pink (#EC4899)
  pink: {
    subtle: '0 0 8px rgba(236, 72, 153, 0.12), 0 0 16px rgba(236, 72, 153, 0.06)',
    medium: '0 0 12px rgba(236, 72, 153, 0.18), 0 0 24px rgba(236, 72, 153, 0.10)',
    strong: '0 0 16px rgba(236, 72, 153, 0.25), 0 0 32px rgba(236, 72, 153, 0.14)',
  },
  // Legacy aliases (deprecated — use semantic names above)
  cyan: {
    subtle: '0 0 8px rgba(14, 165, 233, 0.12), 0 0 16px rgba(14, 165, 233, 0.06)',
    medium: '0 0 12px rgba(14, 165, 233, 0.18), 0 0 24px rgba(14, 165, 233, 0.10)',
    strong: '0 0 16px rgba(14, 165, 233, 0.25), 0 0 32px rgba(14, 165, 233, 0.14)',
  },
  purple: {
    subtle: '0 0 8px rgba(139, 92, 246, 0.12), 0 0 16px rgba(139, 92, 246, 0.06)',
    medium: '0 0 12px rgba(139, 92, 246, 0.18), 0 0 24px rgba(139, 92, 246, 0.10)',
    strong: '0 0 16px rgba(139, 92, 246, 0.25), 0 0 32px rgba(139, 92, 246, 0.14)',
  },
  green: {
    subtle: '0 0 8px rgba(16, 185, 129, 0.12), 0 0 16px rgba(16, 185, 129, 0.06)',
    medium: '0 0 12px rgba(16, 185, 129, 0.18), 0 0 24px rgba(16, 185, 129, 0.10)',
    strong: '0 0 16px rgba(16, 185, 129, 0.25), 0 0 32px rgba(16, 185, 129, 0.14)',
  },
  amber: {
    subtle: '0 0 8px rgba(245, 158, 11, 0.12), 0 0 16px rgba(245, 158, 11, 0.06)',
    medium: '0 0 12px rgba(245, 158, 11, 0.18), 0 0 24px rgba(245, 158, 11, 0.10)',
    strong: '0 0 16px rgba(245, 158, 11, 0.25), 0 0 32px rgba(245, 158, 11, 0.14)',
  },
  red: {
    subtle: '0 0 8px rgba(239, 68, 68, 0.12), 0 0 16px rgba(239, 68, 68, 0.06)',
    medium: '0 0 12px rgba(239, 68, 68, 0.18), 0 0 24px rgba(239, 68, 68, 0.10)',
    strong: '0 0 16px rgba(239, 68, 68, 0.25), 0 0 32px rgba(239, 68, 68, 0.14)',
  },
  // Portal brand glows
  adminGold: {
    subtle: '0 0 8px rgba(212, 160, 23, 0.12), 0 0 16px rgba(212, 160, 23, 0.06)',
    medium: '0 0 12px rgba(212, 160, 23, 0.18), 0 0 24px rgba(212, 160, 23, 0.10)',
    strong: '0 0 16px rgba(212, 160, 23, 0.25), 0 0 32px rgba(212, 160, 23, 0.14)',
  },
  businessBlue: {
    subtle: '0 0 8px rgba(0, 120, 215, 0.12), 0 0 16px rgba(0, 120, 215, 0.06)',
    medium: '0 0 12px rgba(0, 120, 215, 0.18), 0 0 24px rgba(0, 120, 215, 0.10)',
    strong: '0 0 16px rgba(0, 120, 215, 0.25), 0 0 32px rgba(0, 120, 215, 0.14)',
  },
} as const;

export type NeonGlowIntensity = 'subtle' | 'medium' | 'strong';
export type NeonGlowAccent = keyof typeof neonGlow;

/**
 * Create a neon glow effect for a given accent color and intensity
 */
export function createNeonGlow(accent: NeonGlowAccent, intensity: NeonGlowIntensity = 'medium'): string {
  return neonGlow[accent]?.[intensity] || neonGlow.neutral.medium;
}

// ============================================================================
// CSS Keyframe Strings (for injection into styled-components / global CSS)
// ============================================================================

/**
 * Breathing box-shadow pulse (subtle → medium → subtle over 3s).
 * Use with `animation: ${glassBreathing} 3s ease-in-out infinite`.
 */
export const glowBreathingKeyframes = `
@keyframes glowBreathing {
  0%, 100% { box-shadow: 0 0 8px rgba(99,91,255,0.10), 0 0 16px rgba(0,212,255,0.05); }
  50% { box-shadow: 0 0 16px rgba(99,91,255,0.18), 0 0 32px rgba(0,212,255,0.10); }
}` as const;

/**
 * Border color shimmer along a gradient (4s linear infinite).
 */
export const borderShimmerKeyframes = `
@keyframes borderShimmer {
  0% { border-color: rgba(99,91,255,0.4); }
  33% { border-color: rgba(0,212,255,0.4); }
  66% { border-color: rgba(168,85,247,0.4); }
  100% { border-color: rgba(99,91,255,0.4); }
}` as const;

/**
 * Neon sign flicker effect (opacity-based, randomised timings).
 */
export const neonFlickerKeyframes = `
@keyframes neonFlicker {
  0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% { opacity: 1; }
  20%, 21.999%, 63%, 63.999%, 65%, 69.999% { opacity: 0.4; }
}` as const;
